// Golden questions: each one is a question and what it expects the Exchange to do.
// They live in each hub's toolset; /api/v1/golden serves the caller's hubs' lists.
// See Design > Toolsets and tools, "Golden question sets".

import type { GoldenQuestion, HubSummary, TestRunResponse } from "./types.ts";

/** A hub's golden questions by the question (intent) each expects; ones no hub should answer are left out. */
export function goldenByIntent(questions: ReadonlyArray<GoldenQuestion>, hub: string): Map<string, GoldenQuestion[]> {
  const out = new Map<string, GoldenQuestion[]>();
  for (const g of questions) {
    if (g.expectHub !== hub || !g.expectIntent) continue;
    out.set(g.expectIntent, [...(out.get(g.expectIntent) ?? []), g]);
  }
  return out;
}

/** Text compared case- and space-insensitively. */
export const normalize = (text: string) => text.trim().toLowerCase().replace(/\s+/g, " ");

/** The asks the platform itself sends: a hub's example questions and every golden question. The Console's
 *  examples and lessons send exactly these, so they aren't a consumer's own demand. */
export function presetAsks(hub: HubSummary, golden: ReadonlyArray<GoldenQuestion>): Set<string> {
  return new Set([...hub.manifest.intents.flatMap((i) => i.examples), ...golden.map((g) => g.question)].map(normalize));
}

/** A hub's golden set: the questions its own toolset holds, including those it should decline. Hubs never share them. */
export const goldenSet = (questions: ReadonlyArray<GoldenQuestion>, hub: string): GoldenQuestion[] =>
  questions.filter((g) => g.hub === hub);

/** One golden question's test run: what the first hub response did. hub is the hub that responded, and outcome is
 *  null when none did. */
export type GoldenResult = Omit<GoldenQuestion, "hub"> & { outcome: string | null; hub: string | null; intent: string | null; ms: number; detail: string };

export function goldenResult(g: GoldenQuestion, res: TestRunResponse): GoldenResult {
  const q = res.questions[0];
  const r = q?.responses[0];
  return { ...g, outcome: r?._tag ?? null, hub: r?.hub ?? null, intent: r?.intent ?? null, ms: q?.ms ?? 0, detail: r?.detail ?? r?.reason ?? "" };
}

/** The outcome is the one expected, and so are the hub and intent when one is expected: scripts/golden-run.ts's rule. */
export const asExpected = (r: GoldenResult) =>
  r.outcome === r.expectOutcome && (r.expectHub === null || r.hub === r.expectHub) && (r.expectIntent === null || r.intent === r.expectIntent);

/** The time between test runs: half the WarmHub user's CONSUMER_LIMITER budget of 120 Consumer API requests a
 *  minute, so the curator's other requests still fit. See Design > Exchange, "Rate limit". */
export const TEST_RUN_GAP_MS = 1000;
/** The bid each golden question is asked with, in US dollars per million answers, as scripts/golden-run.ts's default. */
export const GOLDEN_BID = 5000;
